import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Inbox } from "lucide-react";
import { useLanguage } from "@/App";
import { translations } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const { lang } = useLanguage();
  const t = translations[lang].admin;
  const [token, setToken] = useState("");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (tok = token) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { "X-Admin-Token": tok } });
      setLeads(data);
    } catch (e) {
      setLeads(null);
      setError(e.response?.status === 401 ? t.unauthorized : t.unauthorized);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0A1128] px-6 py-16 font-body text-white" data-testid="admin-page">
      <div className="mx-auto max-w-4xl">
        <Link
          to="/"
          data-testid="admin-back-link"
          className="mb-10 inline-flex items-center gap-2 text-sm text-white/50 transition-colors duration-300 hover:text-[#D4AF37]"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.back}
        </Link>

        <h1 className="font-display text-4xl font-black tracking-tighter">{t.title}</h1>
        <p className="mt-3 text-sm font-light text-slate-300">{t.sub}</p>

        <div className="mt-8 flex gap-3">
          <input
            data-testid="admin-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={t.tokenPh}
            className="w-full max-w-xs border border-white/15 bg-[#101836] px-4 py-3 text-sm text-white outline-none transition-colors duration-300 focus:border-[#D4AF37]"
          />
          <button
            data-testid="admin-view-leads-button"
            onClick={() => load()}
            className="bg-[#D4AF37] px-6 py-3 text-sm font-semibold text-[#0A1128] transition-colors duration-300 hover:bg-white"
          >
            {t.view}
          </button>
          {leads && (
            <button
              data-testid="admin-refresh-button"
              onClick={() => load()}
              className="flex items-center gap-2 border border-white/15 px-4 py-3 text-sm text-white/70 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {t.refresh}
            </button>
          )}
        </div>

        {error && (
          <p data-testid="admin-error" className="mt-6 text-sm text-red-400">
            {error}
          </p>
        )}

        {leads && leads.length === 0 && (
          <div data-testid="admin-empty" className="mt-12 flex items-center gap-3 text-white/40">
            <Inbox className="h-5 w-5" />
            <p className="text-sm">{t.empty}</p>
          </div>
        )}

        {leads && leads.length > 0 && (
          <div className="mt-12 space-y-px border border-white/10 bg-white/10" data-testid="admin-leads-list">
            {leads.map((lead) => (
              <div key={lead.id} data-testid={`admin-lead-${lead.id}`} className="bg-[#101836] p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display text-lg font-semibold text-white">
                    {lead.name}
                    {lead.company && <span className="ml-3 text-sm font-light text-white/50">{lead.company}</span>}
                  </p>
                  <span className="text-xs text-white/40">
                    {new Date(lead.created_at).toLocaleString(lang === "ko" ? "ko-KR" : "en-US")}
                  </span>
                </div>
                <p className="mt-1 text-sm text-[#D4AF37]">{lead.inquiry_type}</p>
                <p className="mt-2 text-sm text-white/70">
                  <a href={`mailto:${lead.email}`} className="hover:text-[#D4AF37]">{lead.email}</a>
                  {lead.phone && <span className="ml-4">{lead.phone}</span>}
                </p>
                <p className="mt-3 border-l-2 border-[#D4AF37]/40 pl-4 text-sm font-light leading-relaxed text-slate-300">
                  {lead.message}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default Admin;
